// src/components/Sidebar.js
import React, { useState, useEffect, useCallback } from "react";
import {
  FileText,
  Filter,
  X,
  Trash2,
  User,
  HelpCircle,
  Calendar,
  MapPin,
  Building,
  Settings,
} from "lucide-react";
import Dropdown from "./Dropdown";
import apiService from "../services/api";
import {  useNavigate } from 'react-router-dom';

const Sidebar = ({
  filters,
  onFilterChange,
  onClear,
  loading,
  isOpen,
  onClose,
}) => {
  const [cascadedDropdowns, setCascadedDropdowns] = useState({
    sales_area: [],
    entity: [],
    customer_name: [],
    site_name: [],
  });
  const navigate = useNavigate();
  const [independentDropdowns, setIndependentDropdowns] = useState({});
  const [customerMapping, setCustomerMapping] = useState({
    nameToNumber: {},
    nameToNumbers: {},
    numberToName: {},
  });
  const [customerNumbersForName, setCustomerNumbersForName] = useState([]);
  const [loadingDropdowns, setLoadingDropdowns] = useState({});

  // State to track displayed customer name (for UI only)
  const [displayCustomerName, setDisplayCustomerName] = useState("");

  // Independent dropdown fields
  const independentFields = [
    {
      key: "customer_entity",
      label: "Customer account number",
      searchLabel: "customer account number",
      icon: User,
    },
    { key: "country_name", label: "Country", searchLabel: "country", icon: MapPin },
    { key: "failure_reason", label: "Failure reason", searchLabel: "failure reason", icon: Settings },
    { key: "part_number", label: "Part number", searchLabel: "part number", icon: Settings },
    { key: "description", label: "Description", searchLabel: "description", icon: FileText },
    { key: "rig_model", label: "Rig model", searchLabel: "rig model", icon: Building },
    { key: "rig_id", label: "Rig ID", searchLabel: "rig ID", icon: Building },
  ];

  // Format date to dd-mm-yyyy for display
  const formatDateForDisplay = (dateValue) => {
    if (!dateValue) return '';
    
    // If it's already in yyyy-mm-dd format (from input), convert to dd-mm-yyyy
    if (dateValue.includes('-') && dateValue.split('-')[0].length === 4) {
      const [year, month, day] = dateValue.split('-');
      return `${day}-${month}-${year}`;
    }
    
    return dateValue;
  };

  // Validate date range (from date should be before or equal to to date)
  const validateDateRange = (fromDate, toDate) => {
    if (!fromDate || !toDate) return true;
    
    const from = new Date(fromDate);
    const to = new Date(toDate);
    
    return from <= to;
  };

  // Simplified date handler - just pass through to parent
  const handleDateChange = (field, value) => {
    onFilterChange(field, value);
  };

  // Load independent dropdowns on mount - FIXED: Ensure this runs independently
  useEffect(() => {
    const loadIndependentDropdowns = async () => {
      try {
        setLoadingDropdowns((prev) => ({ ...prev, independent: true }));
        
        const data = await apiService.getIndependentDropdowns();
        setIndependentDropdowns(data);

        // Load customer mapping
        const mapping = await apiService.getCustomerMapping();
        //console.log("Customer mapping loaded:", mapping);
        setCustomerMapping({
          nameToNumber: mapping.nameToNumber || {},
          nameToNumbers: mapping.nameToNumbers || {},
          numberToName: mapping.numberToName || {}
        });
        setCustomerNumbersForName([]);
      } catch (error) {
        console.error("Failed to load independent dropdowns:", error);
      } finally {
        setLoadingDropdowns((prev) => ({ ...prev, independent: false }));
      }
    };

    loadIndependentDropdowns();
  }, []); // Empty dependency array - only run on mount

  // Sync display customer name when customer_entity or customer_name changes
  useEffect(() => {
    // Prefer filters.customer_name if available (set during selection)
    if (filters.customer_name) {
      setDisplayCustomerName(filters.customer_name);

      // Preload all numbers for this name so dropdown shows every entity
      const name = filters.customer_name;
      let numbersForName = [];
      if (customerMapping.nameToNumbers && customerMapping.nameToNumbers[name] && Array.isArray(customerMapping.nameToNumbers[name])) {
        numbersForName = customerMapping.nameToNumbers[name];
      } else if (customerMapping.nameToNumber && customerMapping.nameToNumber[name]) {
        numbersForName = [customerMapping.nameToNumber[name]];
      } else {
        const entityValue = filters.customer_entity;
        const firstEntity = Array.isArray(entityValue) ? entityValue[0] : entityValue;
        numbersForName = firstEntity ? [firstEntity] : [];
      }
      setCustomerNumbersForName(numbersForName);
      return;
    }

    // Fall back to reverse lookup from customer_entity
    const entityValue = filters.customer_entity;
    const firstEntity = Array.isArray(entityValue) ? entityValue[0] : entityValue;
    
    if (
      firstEntity &&
      customerMapping.numberToName &&
      customerMapping.numberToName[firstEntity]
    ) {
      const name = customerMapping.numberToName[firstEntity];
      setDisplayCustomerName(name);

      let numbersForName = [];
      if (customerMapping.nameToNumbers && customerMapping.nameToNumbers[name] && Array.isArray(customerMapping.nameToNumbers[name])) {
        numbersForName = customerMapping.nameToNumbers[name];
      } else if (customerMapping.nameToNumber && customerMapping.nameToNumber[name]) {
        numbersForName = [customerMapping.nameToNumber[name]];
      } else {
        numbersForName = [firstEntity];
      }

      setCustomerNumbersForName(numbersForName);
    } else {
      setDisplayCustomerName("");
      setCustomerNumbersForName([]);
    }
  }, [filters.customer_entity, filters.customer_name, customerMapping]);

  // Load cascaded dropdowns when relevant filters change
  const loadCascadedDropdowns = useCallback(async () => {
    try {
      setLoadingDropdowns((prev) => ({ ...prev, cascaded: true }));

      // Build cascaded filters object
      const cascadedFilters = {
        sales_area: filters.sales_area,
        entity: filters.entity,
        customer_entity: filters.customer_entity,
        site_name: filters.site_name,
      };

      // Only include date filters if BOTH are present
      if (filters.date_from && filters.date_to) {
        cascadedFilters.date_from = filters.date_from;
        cascadedFilters.date_to = filters.date_to;
      }

      const data = await apiService.getCascadedDropdowns(cascadedFilters);
      setCascadedDropdowns(data);
    } catch (error) {
      console.error("Failed to load cascaded dropdowns:", error);
    } finally {
      setLoadingDropdowns((prev) => ({ ...prev, cascaded: false }));
    }
  }, [
    filters.sales_area,
    filters.entity,
    filters.customer_entity,
    filters.site_name,
    filters.date_from,
    filters.date_to,
  ]);

  useEffect(() => {
    loadCascadedDropdowns();
  }, [loadCascadedDropdowns]);

  // Handle customer name selection - always filter by number
  const handleCustomerNameChange = (customerName) => {
    if (customerName) {
      // Get all numbers for this name (prefer nameToNumbers array, fall back to single nameToNumber)
      const numbers = (customerMapping.nameToNumbers && customerMapping.nameToNumbers[customerName] && Array.isArray(customerMapping.nameToNumbers[customerName]))
        ? customerMapping.nameToNumbers[customerName]
        : (customerMapping.nameToNumber && customerMapping.nameToNumber[customerName])
        ? [customerMapping.nameToNumber[customerName]]
        : [];

      // Set numbers FIRST so dropdown options are available immediately
      setCustomerNumbersForName(numbers);

      // For multi-select, set all numbers; for single-select keep current if valid
      const currentEntities = Array.isArray(filters.customer_entity) ? filters.customer_entity : (filters.customer_entity ? [filters.customer_entity] : []);
      const validEntities = currentEntities.filter(e => numbers.includes(e));
      const nextValue = validEntities.length > 0 ? validEntities : numbers;

      onFilterChange({ customer_entity: nextValue, customer_name: customerName });
      setDisplayCustomerName(customerName);
    } else {
      // Clear both when empty
      onFilterChange({ customer_entity: [], customer_name: "" });
      setDisplayCustomerName("");
      setCustomerNumbersForName([]);
    }
  };

  // Handle customer number selection - update both but filter by number
  const handleCustomerNumberChange = (value) => {
    // Value is now an array for multi-select
    onFilterChange("customer_entity", value);

    // Update display name if mapping exists
    if (Array.isArray(value) && value.length > 0 && customerMapping.numberToName[value[0]]) {
      setDisplayCustomerName(customerMapping.numberToName[value[0]]);
    } else {
      setDisplayCustomerName("");
    }
  };

  // Get active filters for display
  const getActiveFilters = () => {
    return Object.entries(filters)
      .filter(([key, value]) => {
        // Special handling for date filters - only show if both are present
        if (key === "date_from" || key === "date_to") {
          return filters.date_from && filters.date_to && value && value.trim() !== "";
        }
        
        // Special handling for customer_entity array
        if (key === "customer_entity") {
          return Array.isArray(value) ? value.length > 0 : (value && value.trim() !== "");
        }
        
        return value &&
          typeof value === 'string' &&
          value.trim() !== "" &&
          value !== "(Empty/Not Specified)" &&
          key !== "report_type";
      })
      .map(([key, value]) => {
        // For customer_entity, show both name and number in the active filter display
        if (key === "customer_entity" && displayCustomerName) {
          const entities = Array.isArray(value) ? value : [value];
          const display = entities.length > 1 
            ? `${displayCustomerName} (${entities.join(', ')})`
            : `${displayCustomerName} (${entities[0]})`;
          return [key, display];
        }
        // For date fields, show formatted date
        if ((key === "date_from" || key === "date_to") && value) {
          return [key, formatDateForDisplay(value)];
        }
        return [key, value];
      });
  };

  const removeFilter = (filterKey) => {
    if (filterKey === "customer_entity") {
      // Clear both customer fields
      onFilterChange("customer_entity", []);
      setDisplayCustomerName("");
    } else if (filterKey === "date_from" || filterKey === "date_to") {
      // Clear BOTH date fields when removing either date filter
      onFilterChange("date_from", "");
      onFilterChange("date_to", "");
    } else {
      onFilterChange(filterKey, "");
    }
  };

  const handleClearAll = () => {
    onClear();
    setDisplayCustomerName(""); // Also clear display name
  };

  const activeFilters = getActiveFilters();

  if (!isOpen) return null;

  return (
    <div className="w-80 bg-white shadow-lg h-screen overflow-hidden flex flex-col">
      {/* Header with Close Button */}
      <div className="flex-shrink-0 bg-gray-50 border-b border-gray-200 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center space-x-2">
            {/* User info commented out as in original */}
          </div>
          <button
            onClick={onClose}
            className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-200  transition-colors"
            title="Close filters"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Clear Filters Action */}
        <button
          onClick={handleClearAll}
          disabled={loading}
          className="w-full border border-gray-300 text-gray-700 py-2 px-3  hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm font-medium flex items-center justify-center gap-2"
        >
          <Trash2 className="h-4 w-4" />
          Clear all filters
        </button>
        <p className="text-xs text-gray-500 mt-1 text-center">
          All filters use partial matching and work with general search above
        </p>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-6">
          {/* Active Filters Display */}
          {activeFilters.length > 0 && (
            <div className="p-3 bg-gray-50  border border-gray-200">
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-sm font-medium text-gray-900 flex items-center gap-1">
                  <Filter className="h-3 w-3" />
                  Sidebar filters ({activeFilters.length})
                </h4>
                <button
                  onClick={handleClearAll}
                  className="text-xs text-gray-600 hover:text-black"
                >
                  Clear all
                </button>
              </div>
              <div className="space-y-1">
                {activeFilters.map(([key, value]) => {
                  // Generate a unique key for date filters to ensure both show
                  const displayKey = key === 'date_from' || key === 'date_to' 
                    ? `${key}_${value}` 
                    : key;

                  // Get the proper label from independentFields
                  const getDisplayLabel = (fieldKey) => {
                    const field = independentFields.find(f => f.key === fieldKey);
                    if (field) return field.label;
                    
                    if (fieldKey === "date_from") return "Date from";
                    if (fieldKey === "date_to") return "Date to";
                    
                    const text = fieldKey.replace(/_/g, " ");
                    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
                  };
                    
                  return (
                    <div
                      key={displayKey}
                      className="flex items-center justify-between bg-white px-2 py-1  border text-xs"
                    >
                      <div className="flex-1 min-w-0">
                        <span className="text-gray-600 font-medium block">
                          {getDisplayLabel(key)}
                        </span>
                        <span className="text-gray-900 truncate block">
                          {typeof value === 'string' && value.length > 50
                            ? `${value.substring(0, 50)}...`
                            : value}
                        </span>
                      </div>
                      <button
                        onClick={() => removeFilter(key)}
                        className="ml-2 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Location & Customer Section */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2 tracking-wider">
              <MapPin className="h-4 w-4" />
              Location & customer
            </h3>

            <div className="space-y-2" style={{ textAlign: "left" }}>
              {/* Sales Area */}
              <Dropdown
                label="Sales area"
                value={filters.sales_area || ""}
                onChange={(value) => onFilterChange("sales_area", value)}
                options={cascadedDropdowns.sales_area || []}
                loading={loadingDropdowns.cascaded}
                placeholder="Select sales area"
                disabled={loading}
              />

              {/* Sales Entity */}
              <Dropdown
                label="Sales entity"
                value={filters.entity || ""}
                onChange={(value) => onFilterChange("entity", value)}
                options={cascadedDropdowns.entity || []}
                loading={loadingDropdowns.cascaded}
                placeholder="Select sales entity"
                disabled={loading}
              />

              {/* Customer Name - UI only, filters by number */}
              <Dropdown
                label="Customer name"
                value={displayCustomerName}
                onChange={handleCustomerNameChange}
                options={cascadedDropdowns.customer_name || []}
                loading={loadingDropdowns.cascaded}
                placeholder="Select customer"
                disabled={loading}
              />

              {/* Site Name */}
              <Dropdown
                label="Site name"
                value={filters.site_name || ""}
                onChange={(value) => onFilterChange("site_name", value)}
                options={cascadedDropdowns.site_name || []}
                loading={loadingDropdowns.cascaded}
                placeholder="Select site"
                disabled={loading}
              />
            </div>
          </div>

          {/* Date Filters Section */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2 tracking-wider">
              <Calendar className="h-4 w-4" />
              Time span
            </h3>

            <div className="space-y-2" style={{ textAlign: "left" }}>
              {/* Date fields side by side */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    From date
                  </label>
                  <input
                    type="date"
                    value={filters.date_from || ""}
                    onChange={(e) => handleDateChange('date_from', e.target.value)}
                    className="w-full border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                    disabled={loading}
                    max={filters.date_to || undefined}
                  />
                  {/* Show formatted date below input */}
                  {filters.date_from && (
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDateForDisplay(filters.date_from)}
                    </p>
                  )}
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    To date
                  </label>
                  <input
                    type="date"
                    value={filters.date_to || ""}
                    onChange={(e) => handleDateChange('date_to', e.target.value)}
                    className="w-full border border-gray-300 px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black focus:border-black"
                    disabled={loading}
                    min={filters.date_from || undefined}
                  />
                  {/* Show formatted date below input */}
                  {filters.date_to && (
                    <p className="text-xs text-gray-500 mt-1">
                      {formatDateForDisplay(filters.date_to)}
                    </p>
                  )}
                </div>
              </div>
              
              {/* Show warning if only one date is selected */}
              {((filters.date_from && !filters.date_to) || (!filters.date_from && filters.date_to)) && (
                <p className="text-xs text-amber-600 mt-1 font-medium">
                  ⚠️ Both dates required for filtering
                </p>
              )}
              
              {/* Show error if date range is invalid */}
              {filters.date_from && filters.date_to && !validateDateRange(filters.date_from, filters.date_to) && (
                <p className="text-xs text-red-600 mt-1 font-medium">
                  ❌ From date must be before or equal to To date
                </p>
              )}
            </div>
          </div>

          {/* Other Filters - FIXED: Debug logging and proper loading state */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2 tracking-wider">
              <Settings className="h-4 w-4" />
              Other filters
            </h3>
            
            {/* Debug info - remove this in production */}
            {/* {process.env.NODE_ENV === 'development' && (
              <div className="text-xs text-gray-500 mb-2">
                Independent loading: {loadingDropdowns.independent ? 'true' : 'false'}, 
                Options count: {Object.keys(independentDropdowns).length}
                <br />
                Customer numbers for name: {JSON.stringify(customerNumbersForName)}
                <br />
                Display name: {displayCustomerName}
              </div>
            )} */}
            
            <div className="space-y-2" style={{ textAlign: "left" }}>
              {independentFields.map((field) => {
                const fieldOptions = independentDropdowns[field.key] || [];
                const scopedOptions =
                  field.key === "customer_entity" && customerNumbersForName.length
                    ? customerNumbersForName
                    : fieldOptions;
                
                // Debug log for customer_entity
                // if (field.key === "customer_entity" && process.env.NODE_ENV === 'development') {
                //   console.log('Customer Entity Dropdown:', {
                //     customerNumbersForName,
                //     fieldOptions: fieldOptions.length,
                //     scopedOptions: scopedOptions.length,
                //     scopedOptionsValues: scopedOptions
                //   });
                // }
                
                return (
                  <Dropdown
                    key={field.key}
                    label={field.label}
                    value={
                      field.key === "customer_entity"
                        ? filters.customer_entity || []
                        : filters[field.key] || ""
                    }
                    onChange={(value) =>
                      field.key === "customer_entity"
                        ? handleCustomerNumberChange(value)
                        : onFilterChange(field.key, value)
                    }
                    options={scopedOptions}
                    loading={loadingDropdowns.independent}
                    placeholder={`Search ${field.searchLabel}...`}
                    disabled={loading || loadingDropdowns.independent}
                    multiSelect={field.key === "customer_entity"}
                  />
                );
              })}
            </div>
          </div>

          {/* Info Box about General Search */}
          <div className="p-3 bg-amber-50 border border-amber-200 ">
            <div className="flex items-start gap-2">
              <div className="text-left">
                <h4 className="text-sm font-medium text-amber-900 text-left">
                  Search behavior
                </h4>
                <p className="text-xs text-amber-800 mt-1 text-left">
                  • General search bar: searches across site names, customer
                  names, failure reasons, and serial numbers
                </p>
                <p className="text-xs text-amber-800 mt-1 text-left">
                  • Sidebar filters: partial matching within specific fields
                </p>
                <p className="text-xs text-amber-800 mt-1 text-left">
                  • Both are case-insensitive and work together
                </p>
                <p className="text-xs text-amber-800 mt-1 font-medium text-left">
                  • Customer filtering always uses account number internally
                </p>
                <p className="text-xs text-amber-800 mt-1 font-medium text-left">
                  • Date range filtering requires both From and To dates
                </p>
                <p className="text-xs text-amber-800 mt-1 font-medium text-left">
                  • Records are filtered inclusively between the selected dates
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Menu Items - Fixed at bottom */}
      <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50">
        <div className="p-3 space-y-2">
          <div className="flex items-center space-x-3 p-2 bg-gray-900  border border-gray-900">
            <div className="w-6 h-6 flex items-center justify-center">
              <div className="w-4 h-4 bg-white  flex items-center justify-center">
                <FileText className="h-3 w-3 text-black" />
              </div>
            </div>
            <span className="text-sm text-white font-medium">My reports</span>
          </div>

          {/* <div
            onClick={() => navigate("/settings")}
            className="flex items-center space-x-3 p-2 hover:bg-gray-100 cursor-pointer group"
          >
            <div className="w-6 h-6 flex items-center justify-center">
              <div className="w-4 h-4 border border-gray-400 flex items-center justify-center group-hover:border-gray-600">
                <Settings className="h-3 w-3 text-gray-600" />
              </div>
            </div>
            <span className="text-sm text-gray-700 font-medium">Settings</span>
          </div> */}

          <div className="flex items-center space-x-3 p-2 hover:bg-gray-100  cursor-pointer group">
            <div className="w-6 h-6 flex items-center justify-center">
              <div className="w-4 h-4 border border-gray-400  flex items-center justify-center group-hover:border-gray-600">
                <HelpCircle className="h-3 w-3 text-gray-600" />
              </div>
            </div>
            <span className="text-sm text-gray-700 font-medium">Help</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sidebar;